import React, { useState, useEffect, useCallback } from "react";
import { useAgentActivitySocket, useCommentSocket } from "../context/SocketContext";
import { getProfilePicture } from "../utils/avatar";

const TYPING_TIMEOUT = 8000; // Auto-hide after 8 seconds

const TypingIndicator = ({ messageId }) => {
  const [typingUsers, setTypingUsers] = useState({}); // { username: timeoutId }

  // Listen for typing events
  const handleAgentActivity = useCallback(
    (data) => {
      if (
        data.activityType === "typing" &&
        data.data?.messageId === messageId &&
        data.data?.username
      ) {
        const username = data.data.username;
        setTypingUsers((prev) => {
          // Clear existing timeout for this user
          if (prev[username]) {
            clearTimeout(prev[username]);
          }
          // Set new timeout
          const timeoutId = setTimeout(() => {
            setTypingUsers((p) => {
              const next = { ...p };
              delete next[username];
              return next;
            });
          }, TYPING_TIMEOUT);
          return { ...prev, [username]: timeoutId };
        });
      }
    },
    [messageId]
  );

  // Clear typing when a new comment arrives on this post
  const handleNewComment = useCallback(
    (comment) => {
      if (comment.messageId === messageId && comment.author?.username) {
        setTypingUsers((prev) => {
          const next = { ...prev };
          if (next[comment.author.username]) {
            clearTimeout(next[comment.author.username]);
            delete next[comment.author.username];
          }
          return next;
        });
      }
    },
    [messageId]
  );

  useAgentActivitySocket(handleAgentActivity);
  useCommentSocket(handleNewComment);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(typingUsers).forEach(clearTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const names = Object.keys(typingUsers);
  if (names.length === 0) return null;

  const displayName =
    names.length === 1
      ? names[0]
      : names.length === 2
      ? `${names[0]} and ${names[1]}`
      : `${names[0]} and ${names.length - 1} others`;

  return (
    <div className="flex items-center space-x-2 py-2 px-1">
      <div className="flex -space-x-1.5">
        {names.slice(0, 2).map((name) => (
          <img
            key={name}
            src={getProfilePicture(name)}
            alt={name}
            className="w-5 h-5 rounded-full border border-spiders-bg-surface"
          />
        ))}
      </div>
      <div className="flex items-center space-x-1.5">
        <span className="text-xs text-spiders-text-muted">{displayName}</span>
        <span className="flex items-center space-x-0.5">
          <span className="typing-dot" />
          <span className="typing-dot" />
          <span className="typing-dot" />
        </span>
      </div>
    </div>
  );
};

export default TypingIndicator;
